import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { programKeys } from './usePrograms';
import type { Program, ProgramLevel, ProgramSubject } from '@lms/shared/types';
import {
  applyVerifiedSubjectPrerequisite,
  updateSubjectWithVerification,
} from '../lib/subject-prerequisite-save';

export const subjectKeys = {
  all: ['program-subjects'] as const,
  lists: () => [...subjectKeys.all, 'list'] as const,
  list: (stageId: string) => [...subjectKeys.all, 'list', stageId] as const,
  details: () => [...subjectKeys.all, 'detail'] as const,
  detail: (id: string) => [...subjectKeys.details(), id] as const,
};

export function useSubjects(stageId: string) {
  return useQuery({
    queryKey: subjectKeys.list(stageId),
    queryFn: () => apiClient.programSubjects.list(stageId),
    enabled: !!stageId,
  });
}

export function useCreateSubject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      programId: string;
      data: Partial<ProgramSubject> & { stageId: string; name: string };
    }) => apiClient.programSubjects.create(variables.data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: programKeys.detail(variables.programId),
      });
      void queryClient.invalidateQueries({ queryKey: subjectKeys.lists() });
    },
  });
}

export function useUpdateSubject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: string; programId: string; data: Partial<ProgramSubject> }) =>
      updateSubjectWithVerification(apiClient.programSubjects, variables.id, variables.data),
    onSuccess: async (verified, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: programKeys.detail(variables.programId) }),
        queryClient.invalidateQueries({ queryKey: subjectKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: subjectKeys.details() }),
      ]);
      // Program graph/list queries can still contain an older relation. Use the
      // independently verified subject, not the selected form value, after refetch.
      if (variables.data.prerequisiteSubjectId !== undefined) {
        queryClient.setQueryData(subjectKeys.detail(variables.id), verified);
        queryClient.setQueryData<Program>(programKeys.detail(variables.programId), current =>
          current
            ? { ...current, levels: applyVerifiedSubjectPrerequisite(current.levels, verified) }
            : current
        );
        queryClient.setQueryData<ProgramLevel[]>(programKeys.levels(variables.programId), current =>
          applyVerifiedSubjectPrerequisite(current, verified)
        );
        queryClient.setQueriesData<ProgramSubject[]>({ queryKey: subjectKeys.lists() }, current =>
          current?.map(subject =>
            subject.id === verified.id
              ? {
                  ...subject,
                  prerequisiteSubjectId: verified.prerequisiteSubjectId,
                  prerequisiteSubject: verified.prerequisiteSubject ?? undefined,
                }
              : subject
          )
        );
      }
    },
  });
}

export function useDeleteSubject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: string; programId: string }) =>
      apiClient.programSubjects.delete(variables.id),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: programKeys.detail(variables.programId),
      });
      void queryClient.invalidateQueries({ queryKey: subjectKeys.lists() });
    },
  });
}

export function useReorderSubjects() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      programId: string;
      items: { id: string; order: number; expectedOrder: number }[];
    }) => apiClient.programSubjects.reorder(variables.items),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: programKeys.detail(variables.programId) }),
        queryClient.invalidateQueries({ queryKey: subjectKeys.lists() }),
      ]);
    },
    onError: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: programKeys.detail(variables.programId) });
    },
  });
}
